import React, { memo, useCallback } from 'react';
import {
  Reply as ReplyModel,
  ReplyStatus,
  ReplyStatusStatusName,
} from '@app/models';
import { Info } from '@app/components';
import { currencyFormat, dateFormat, fullName } from '@app/helpers';
import { IconFlag } from '@app/icons';
import { cn } from 'lib/utils';

interface Props {
  reply: ReplyModel;
  onClick: (reply: ReplyModel) => void;
  horizontal?: boolean;
}

// Унифицированная функция для цветов статусов (как в компоненте Doc)
const replyStatusColor = (status: ReplyStatus) => {
  switch (status) {
    case 10: // Новый
      return 'bg-primary text-blue-500';
    case 20: // Принят
      return 'bg-green-500 text-green-700';
    case 30: // Отклонен
      return 'bg-red-500 text-red-700';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

function Reply(props: Props) {
  const { reply, onClick, horizontal = false } = props;

  const onClickReply = useCallback(() => {
    onClick(reply);
  }, [onClick, reply]);

  // Получаем классы для текущего статуса
  const statusClasses = replyStatusColor(reply.status);
  const [bgClass, textClass] = statusClasses.split(' ');

  // Горизонтальный вид (список)
  if (horizontal) {
    return (
      <div
        key={reply.id}
        className={cn(
          'flex gap-5 p-4 rounded-xl bg-muted/40',
          'items-center',
          'mb-2'
        )}
      >
        <div
          className={cn(
            'grid items-center cursor-pointer w-full',
            'grid-cols-1 sm:grid-cols-5 gap-4'
          )}
          onClick={onClickReply}
        >
          <div className="flex items-center">
            <span className={cn('w-2 h-2 rounded-full mr-2', bgClass)} />
            <span className={cn('text-xs font-medium', textClass)}>
              {ReplyStatusStatusName[reply.status]}
            </span>
          </div>
          <Info
            label="Автор"
            value={
              reply?.companyId ? reply.company?.name : fullName(reply.owner)
            }
            small={true}
          />
          <Info
            label="Дата подачи"
            value={dateFormat(reply.createdAt)}
            small={true}
            displayedLength={22}
          />
          <Info label="Цена" value={currencyFormat(reply.price)} small={true} />
          <Info
            label="Статус"
            value={ReplyStatusStatusName[reply.status]}
            small={true}
            displayedLength={22}
          />
        </div>
        {reply.pinned && (
          <div className="flex items-center justify-end cursor-pointer">
            <IconFlag className="w-6 h-6 text-primary" />
          </div>
        )}
      </div>
    );
  }

  // Карточный вид (сетка)
  return (
    <div
      key={reply.id}
      className={cn('flex flex-col gap-3 p-4 rounded-xl bg-muted')}
    >
      <div className="cursor-pointer" onClick={onClickReply}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            <span className={cn('w-2 h-2 rounded-full mr-2', bgClass)} />
            <span className={cn('text-xs font-medium', textClass)}>
              {ReplyStatusStatusName[reply.status]}
            </span>
          </div>
          {reply.pinned && <IconFlag className="w-6 h-6 text-primary" />}
        </div>

        <div className="space-y-3">
          <Info
            label="Автор"
            value={
              reply?.companyId ? reply.company?.name : fullName(reply.owner)
            }
            small={true}
          />
          <Info
            label="Дата подачи"
            value={dateFormat(reply.createdAt)}
            small={true}
            displayedLength={22}
          />
          <Info label="Цена" value={currencyFormat(reply.price)} small={true} />
        </div>
      </div>
    </div>
  );
}

export default memo(Reply);
